import React from "react"
import styled from "styled-components"
import { graphql } from "gatsby"
import Img from "gatsby-image"
import { useIntl } from "gatsby-plugin-intl"
import {
  FiArrowRight,
  FiArrowDown,
  FiLayers,
  FiShield,
  FiUsers,
  FiCode,
  FiCpu,
  FiCloud,
  FiGitBranch,
} from "react-icons/fi"
import PageMetadata from "../../components/PageMetadata"
import Link from "../../components/Link"
import { translateMessageId } from "../../utils/translations"

const Page = styled.div`
  width: 100%;
  color: ${({ theme }) => theme.colors.text};
  h1,
  h2,
  h3 {
    font-family: inherit;
    font-style: normal;
    font-weight: 500;
  }
  h2 {
    font-size: clamp(2rem, 3.6vw, 3.1rem);
    line-height: 1.12;
    letter-spacing: -0.04em;
    margin: 0 0 1.5rem;
    text-wrap: balance;
  }
  h3 {
    letter-spacing: -0.025em;
  }
  a:focus-visible {
    outline: 3px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 5px;
  }
  a:hover svg {
    fill: none;
  }
  section[id] {
    scroll-margin-top: 6rem;
  }
`
const Container = styled.div`
  width: min(100%, 1200px);
  margin: 0 auto;
  padding: 0 2.5rem;
  @media (max-width: 600px) {
    padding: 0 1.25rem;
  }
`
const Kicker = styled.div`
  color: ${({ theme }) => theme.colors.primary};
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.13em;
  text-transform: uppercase;
  margin-bottom: 1.5rem;
  line-height: 1.7;
`
const Copy = styled.p`
  color: ${({ theme }) => theme.colors.text200};
  font-size: 1.05rem;
  line-height: 1.8;
  margin: 0 0 1.25rem;
  &:last-child {
    margin-bottom: 0;
  }
`
const Hero = styled.header`
  position: relative;
  isolation: isolate;
  overflow: hidden;
  background: #071723;
  color: #fff;
  &::after {
    content: "";
    position: absolute;
    inset: 0;
    z-index: -1;
    pointer-events: none;
    background: linear-gradient(
      90deg,
      #061521f2 0%,
      #071724d9 32%,
      #08172473 60%,
      #0918250d 100%
    );
  }
  ${Container} {
    min-height: 650px;
    padding-block: 5rem 3rem;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
  }
  ${Kicker} {
    color: #c6d9e8;
    display: flex;
    align-items: center;
    gap: 0.8rem;
    &::before {
      content: "";
      width: 2rem;
      height: 2px;
      background: #9ecfea;
      flex-shrink: 0;
    }
  }
  h1 {
    color: #fff;
    font-size: clamp(3rem, 5.2vw, 4.6rem);
    line-height: 1.04;
    letter-spacing: -0.05em;
    max-width: 700px;
    margin: 0 0 1.75rem;
    span {
      display: block;
      color: #b9e0f5;
    }
  }
  p {
    max-width: 505px;
    color: #d3dfe9;
    font-size: 1.1rem;
    line-height: 1.8;
    margin: 0 0 2rem;
  }
  small {
    color: #b8cddd;
    font-size: 0.75rem;
    line-height: 1.7;
    letter-spacing: 0.02em;
    margin-top: 2.75rem;
  }
  a:focus-visible {
    outline-color: #b9e0f5;
  }
  @media (max-width: 750px) {
    display: flex;
    flex-direction: column;
    &::after {
      display: none;
    }
    ${Container} {
      min-height: auto;
      padding-block: 3.5rem 2.5rem;
    }
    h1 {
      font-size: clamp(2.6rem, 8vw, 3.8rem);
    }
    p {
      font-size: 1rem;
    }
    small {
      margin-top: 2rem;
    }
  }
`
const HeroImage = styled(Img)`
  position: absolute !important;
  inset: 0;
  z-index: -2;
  height: 100%;
  width: 100%;
  @media (max-width: 750px) {
    position: relative !important;
    inset: auto;
    z-index: auto;
    order: 2;
    height: 270px;
    img {
      object-position: 85% center !important;
    }
  }
`
const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 1.25rem 1.75rem;
`
const Primary = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.1rem;
  padding: 1rem 1.25rem;
  color: #102335 !important;
  background: #fff;
  border: 1px solid #fff;
  font-size: 0.9rem;
  font-weight: 600;
  line-height: 1.5;
  text-decoration: none;
  svg {
    flex-shrink: 0;
  }
  &:hover {
    background: #d8ecf9;
    border-color: #d8ecf9;
  }
`
const Secondary = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.85rem;
  color: #fff !important;
  padding: 0.65rem 0;
  border-bottom: 1px solid #8fa9bb;
  font-size: 0.9rem;
  text-decoration: none;
  svg {
    flex-shrink: 0;
  }
  &:hover {
    border-color: #fff;
  }
`
const SectionNav = styled.nav`
  background: ${({ theme }) => theme.colors.ednBackground};
  border-bottom: 1px solid ${({ theme }) => theme.colors.lightBorder};
  ${Container} {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
  a {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    padding: 1.6rem 1.25rem;
    border-right: 1px solid ${({ theme }) => theme.colors.lightBorder};
    color: ${({ theme }) => theme.colors.text};
    font-size: 0.85rem;
    text-decoration: none;
  }
  a:first-child {
    border-left: 1px solid ${({ theme }) => theme.colors.lightBorder};
  }
  a:hover {
    background: ${({ theme }) => theme.colors.background};
  }
  svg {
    flex-shrink: 0;
  }
  @media (max-width: 850px) {
    ${Container} {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
    a {
      border-bottom: 1px solid ${({ theme }) => theme.colors.lightBorder};
    }
    a:nth-child(3) {
      border-left: 1px solid ${({ theme }) => theme.colors.lightBorder};
    }
    a:nth-child(n + 3) {
      border-bottom: 0;
    }
  }
  @media (max-width: 400px) {
    a {
      font-size: 0.75rem;
      padding: 1rem 0.6rem;
      gap: 0.5rem;
    }
  }
`
const Identity = styled.section`
  padding: 6rem 0;
  @media (max-width: 650px) {
    padding: 3.5rem 0;
  }
`
const IntroGrid = styled.div`
  display: grid;
  grid-template-columns: 0.9fr 1.1fr;
  gap: 5rem;
  align-items: start;
  margin-bottom: 4rem;
  h2 {
    max-width: 430px;
  }
  @media (max-width: 850px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
    margin-bottom: 3rem;
  }
`
const PurposeGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  border-top: 1px solid ${({ theme }) => theme.colors.lightBorder};
  border-bottom: 1px solid ${({ theme }) => theme.colors.lightBorder};
  article {
    padding: 2.5rem 2.5rem 2.5rem 0;
  }
  article + article {
    border-left: 1px solid ${({ theme }) => theme.colors.lightBorder};
    padding-left: 2.5rem;
    padding-right: 0;
  }
  ${Kicker} {
    margin-bottom: 1rem;
  }
  h3 {
    font-size: 1.7rem;
    line-height: 1.2;
    margin: 0 0 1rem;
    max-width: 390px;
  }
  p {
    font-size: 0.98rem;
  }
  @media (max-width: 700px) {
    grid-template-columns: 1fr;
    article,
    article + article {
      padding: 2rem 0;
      border-left: 0;
    }
    article + article {
      border-top: 1px solid ${({ theme }) => theme.colors.lightBorder};
    }
  }
`
const Principles = styled.section`
  background: #0b2032;
  color: #fff;
  padding: 4.5rem 0;
  ${Kicker} {
    color: #9ecfea;
  }
  h2 {
    color: #fff;
    max-width: 700px;
    margin-bottom: 3rem;
  }
  @media (max-width: 650px) {
    padding: 3.5rem 0;
  }
`
const PrinciplesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 3rem;
  article {
    border-top: 1px solid #456071;
    padding-top: 1.75rem;
  }
  svg {
    width: 1.6rem;
    height: 1.6rem;
    color: #a9d9f5;
    margin-bottom: 1.25rem;
  }
  h3 {
    font-size: 1.3rem;
    color: #fff;
    line-height: 1.3;
    margin: 0 0 1rem;
  }
  p {
    color: #c2d0dc;
    font-size: 0.95rem;
    line-height: 1.8;
    margin: 0;
  }
  @media (max-width: 800px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`
const Method = styled.section`
  padding: 6rem 0;
  @media (max-width: 650px) {
    padding: 3.5rem 0;
  }
`
const MethodGrid = styled.div`
  display: grid;
  grid-template-columns: 0.95fr 1.05fr;
  gap: 4rem;
  align-items: stretch;
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 2.5rem;
  }
`
const MethodVisual = styled.figure`
  min-width: 0;
  margin: 0;
  position: relative;
  background: #0a1c2a;
  > .gatsby-image-wrapper {
    position: absolute !important;
    inset: 0;
    height: 100%;
  }
  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(
      0deg,
      #04121ff5 0%,
      #0516238c 27%,
      transparent 60%
    );
    pointer-events: none;
  }
  figcaption {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 1;
    padding: 2.5rem;
    border-bottom: 3px solid #9ecfea;
  }
  small {
    display: block;
    color: #a9d9f5;
    font-size: 0.65rem;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    margin-bottom: 1rem;
  }
  strong {
    display: block;
    font-size: 1.75rem;
    line-height: 1.2;
    font-weight: 500;
    letter-spacing: -0.035em;
    color: #fff;
    max-width: 350px;
  }
  @media (max-width: 900px) {
    min-height: 400px;
  }
  @media (max-width: 500px) {
    min-height: 330px;
    figcaption {
      padding: 1.5rem;
    }
    strong {
      font-size: 1.5rem;
    }
  }
`
const Steps = styled.ol`
  padding: 0;
  margin: 2rem 0 0;
  list-style: none;
  li {
    display: grid;
    grid-template-columns: 2rem 1fr;
    gap: 1rem;
    margin: 0;
    padding: 1.5rem 0;
    border-top: 1px solid ${({ theme }) => theme.colors.lightBorder};
    &::before {
      display: none;
    }
    &:last-child {
      padding-bottom: 0;
    }
  }
  span {
    font-size: 0.75rem;
    color: ${({ theme }) => theme.colors.primary};
    padding-top: 0.2rem;
  }
  h3 {
    font-size: 1.15rem;
    margin: 0 0 0.5rem;
    line-height: 1.3;
  }
  p {
    color: ${({ theme }) => theme.colors.text200};
    font-size: 0.92rem;
    line-height: 1.75;
    margin: 0;
  }
`
const Capabilities = styled.section`
  padding: 5rem 0;
  background: ${({ theme }) => theme.colors.ednBackground};
  border-block: 1px solid ${({ theme }) => theme.colors.lightBorder};
  @media (max-width: 650px) {
    padding: 3.5rem 0;
  }
`
const SectionHeading = styled.div`
  display: grid;
  grid-template-columns: 1.3fr 0.7fr;
  gap: 4rem;
  align-items: end;
  margin-bottom: 3rem;
  h2 {
    max-width: 620px;
    margin-bottom: 0;
  }
  @media (max-width: 800px) {
    grid-template-columns: 1fr;
    gap: 1.25rem;
  }
`
const CapabilityGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 2rem 3.5rem;
  article {
    padding-top: 2rem;
    border-top: 1px solid ${({ theme }) => theme.colors.lightBorder};
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  }
  article > svg {
    width: 1.6rem;
    height: 1.6rem;
    color: ${({ theme }) => theme.colors.primary};
    margin-bottom: 1.5rem;
  }
  h3 {
    font-size: 1.55rem;
    line-height: 1.2;
    margin: 0 0 1rem;
  }
  p {
    font-size: 0.98rem;
    margin-bottom: 1.25rem;
  }
  @media (max-width: 700px) {
    grid-template-columns: 1fr;
    gap: 2.5rem;
  }
`
const TextLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  font-weight: 600;
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.text};
  padding: 0.5rem 0;
  text-decoration: none;
  border-bottom: 1px solid ${({ theme }) => theme.colors.lightBorder};
  margin-top: auto;
  svg {
    flex-shrink: 0;
  }
  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
  }
`
const ProductCallout = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 2rem;
  margin-top: 3.5rem;
  padding-top: 2rem;
  border-top: 1px solid ${({ theme }) => theme.colors.lightBorder};
  h3 {
    font-size: 1.2rem;
    line-height: 1.3;
    margin: 0 0 0.5rem;
  }
  p {
    font-size: 0.92rem;
    margin: 0;
  }
  a {
    flex-shrink: 0;
    margin-top: 0;
  }
  @media (max-width: 700px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
`
const Partners = styled.section`
  padding: 4.5rem 0;
  ${Container} {
    display: grid;
    grid-template-columns: 1.05fr 0.95fr;
    gap: 4rem;
    align-items: center;
  }
  h2 {
    font-size: clamp(1.8rem, 3vw, 2.5rem);
    margin-bottom: 0;
  }
  @media (max-width: 800px) {
    ${Container} {
      grid-template-columns: 1fr;
      gap: 1.5rem;
    }
  }
  @media (max-width: 650px) {
    padding: 3.5rem 0;
  }
`
const Conversation = styled.section`
  background: #091b2a;
  color: #fff;
  padding: 4.5rem 0;
  border-top: 3px solid #9ecfea;
  ${Container} {
    display: grid;
    grid-template-columns: 1.15fr 0.85fr;
    gap: 4rem;
    align-items: center;
  }
  ${Kicker} {
    color: #a9d9f5;
  }
  h2 {
    color: #fff;
    margin-bottom: 0;
  }
  p {
    color: #c3d2df;
    margin-bottom: 2rem;
  }
  ${Actions} {
    flex-direction: column;
    align-items: flex-start;
  }
  a:focus-visible {
    outline-color: #a9d9f5;
  }
  @media (max-width: 800px) {
    ${Container} {
      grid-template-columns: 1fr;
      gap: 1.5rem;
    }
  }
  @media (max-width: 650px) {
    padding: 3.5rem 0;
  }
`

const principles = [
  { id: "rigor", icon: FiLayers },
  { id: "security", icon: FiShield },
  { id: "clarity", icon: FiUsers },
]
const capabilities = [
  { id: "software", icon: FiCode, to: "/desarrollo/" },
  { id: "ai", icon: FiCpu, to: "/ai-service/" },
  { id: "cloud", icon: FiCloud, to: "/arquitectura/detalle/" },
  { id: "security", icon: FiGitBranch, to: "/devsecops/" },
]
const navigation = [
  { id: "identity", to: "#esencia" },
  { id: "method", to: "#como-trabajamos" },
  { id: "capabilities", to: "#capacidades" },
  { id: "contact", to: "#conversemos" },
]
const QuienesSomosPage = ({ data }) => {
  const intl = useIntl()
  const t = (id) => translateMessageId(`company-${id}`, intl)
  return (
    <Page>
      <PageMetadata
        title={t("meta-title")}
        description={t("meta-description")}
        image={data.collaboration.childImageSharp.fixed.src}
      />
      <Hero>
        <HeroImage
          fluid={data.collaboration.childImageSharp.fluid}
          alt={t("hero-alt")}
          loading="eager"
          fadeIn={false}
          imgStyle={{ objectPosition: "72% center" }}
        />
        <Container>
          <Kicker>{t("eyebrow")}</Kicker>
          <h1>
            {t("hero-title")} <span>{t("hero-accent")}</span>
          </h1>
          <p>{t("hero-intro")}</p>
          <Actions>
            <Primary to="#esencia">
              {t("hero-primary")}
              <FiArrowDown aria-hidden="true" />
            </Primary>
            <Secondary to="#conversemos">
              {t("hero-secondary")}
              <FiArrowRight aria-hidden="true" />
            </Secondary>
          </Actions>
          <small>{t("hero-caption")}</small>
        </Container>
      </Hero>
      <SectionNav aria-label={t("nav-label")}>
        <Container>
          {navigation.map(({ id, to }) => (
            <Link key={id} to={to}>
              {t(`nav-${id}`)}
              <FiArrowDown aria-hidden="true" />
            </Link>
          ))}
        </Container>
      </SectionNav>
      <Identity id="esencia" aria-labelledby="company-identity-title">
        <Container>
          <IntroGrid>
            <div>
              <Kicker>{t("identity-eyebrow")}</Kicker>
              <h2 id="company-identity-title">{t("identity-title")}</h2>
            </div>
            <div>
              <Copy>{t("identity-intro")}</Copy>
              <Copy>{t("identity-body")}</Copy>
            </div>
          </IntroGrid>
          <PurposeGrid>
            {["mission", "vision"].map((id) => (
              <article key={id} aria-labelledby={`company-${id}-title`}>
                <Kicker>{t(`${id}-label`)}</Kicker>
                <h3 id={`company-${id}-title`}>{t(`${id}-title`)}</h3>
                <Copy>{t(`${id}-description`)}</Copy>
              </article>
            ))}
          </PurposeGrid>
        </Container>
      </Identity>
      <Principles aria-labelledby="company-principles-title">
        <Container>
          <Kicker>{t("principles-eyebrow")}</Kicker>
          <h2 id="company-principles-title">{t("principles-title")}</h2>
          <PrinciplesGrid>
            {principles.map(({ id, icon: Icon }) => (
              <article key={id}>
                <Icon aria-hidden="true" />
                <h3>{t(`principle-${id}-title`)}</h3>
                <p>{t(`principle-${id}-description`)}</p>
              </article>
            ))}
          </PrinciplesGrid>
        </Container>
      </Principles>
      <Method id="como-trabajamos" aria-labelledby="company-method-title">
        <Container>
          <MethodGrid>
            <MethodVisual>
              <Img
                fluid={data.projectConversation.childImageSharp.fluid}
                alt={t("method-alt")}
                imgStyle={{ objectPosition: "52% center" }}
              />
              <figcaption>
                <small>{t("method-caption-label")}</small>
                <strong>{t("method-caption")}</strong>
              </figcaption>
            </MethodVisual>
            <div>
              <Kicker>{t("method-eyebrow")}</Kicker>
              <h2 id="company-method-title">{t("method-title")}</h2>
              <Copy>{t("method-intro")}</Copy>
              <Steps>
                {["understand", "design", "build", "evolve"].map((id, i) => (
                  <li key={id}>
                    <span aria-hidden="true">0{i + 1}</span>
                    <div>
                      <h3>{t(`step-${id}-title`)}</h3>
                      <p>{t(`step-${id}-description`)}</p>
                    </div>
                  </li>
                ))}
              </Steps>
            </div>
          </MethodGrid>
        </Container>
      </Method>
      <Capabilities
        id="capacidades"
        aria-labelledby="company-capabilities-title"
      >
        <Container>
          <SectionHeading>
            <div>
              <Kicker>{t("capabilities-eyebrow")}</Kicker>
              <h2 id="company-capabilities-title">{t("capabilities-title")}</h2>
            </div>
            <Copy>{t("capabilities-intro")}</Copy>
          </SectionHeading>
          <CapabilityGrid>
            {capabilities.map(({ id, icon: Icon, to }) => (
              <article key={id}>
                <Icon aria-hidden="true" />
                <h3>{t(`capability-${id}-title`)}</h3>
                <Copy>{t(`capability-${id}-description`)}</Copy>
                <TextLink to={to}>
                  {t(`capability-${id}-link`)}
                  <FiArrowRight aria-hidden="true" />
                </TextLink>
              </article>
            ))}
          </CapabilityGrid>
          <ProductCallout>
            <div>
              <h3>{t("products-title")}</h3>
              <Copy>{t("products-description")}</Copy>
            </div>
            <TextLink to="/productos/">
              {t("products-link")}
              <FiArrowRight aria-hidden="true" />
            </TextLink>
          </ProductCallout>
        </Container>
      </Capabilities>
      <Partners aria-labelledby="company-partners-title">
        <Container>
          <div>
            <Kicker>{t("partners-eyebrow")}</Kicker>
            <h2 id="company-partners-title">{t("partners-title")}</h2>
          </div>
          <div>
            <Copy>{t("partners-description")}</Copy>
            <TextLink to="/aliados/">
              {t("partners-link")}
              <FiArrowRight aria-hidden="true" />
            </TextLink>
          </div>
        </Container>
      </Partners>
      <Conversation
        id="conversemos"
        aria-labelledby="company-conversation-title"
      >
        <Container>
          <div>
            <Kicker>{t("contact-eyebrow")}</Kicker>
            <h2 id="company-conversation-title">{t("contact-title")}</h2>
          </div>
          <div>
            <Copy>{t("contact-description")}</Copy>
            <Actions>
              <Primary to="/#contacto">
                {t("contact-primary")}
                <FiArrowRight aria-hidden="true" />
              </Primary>
              <Secondary
                to={`mailto:gerencia@newinntech.com?subject=${encodeURIComponent(
                  t("contact-subject")
                )}`}
                hideArrow
              >
                {t("contact-secondary")}
                <FiArrowRight aria-hidden="true" />
              </Secondary>
            </Actions>
          </div>
        </Container>
      </Conversation>
    </Page>
  )
}
export default QuienesSomosPage
export const query = graphql`
  query CompanyAboutPageQuery {
    collaboration: file(
      relativePath: { eq: "about/engineering-collaboration-v1.png" }
    ) {
      childImageSharp {
        fluid(maxWidth: 1920, quality: 85) {
          ...GatsbyImageSharpFluid_withWebp
        }
        fixed(width: 1200) {
          src
        }
      }
    }
    projectConversation: file(
      relativePath: { eq: "about/project-conversation-v1.png" }
    ) {
      childImageSharp {
        fluid(maxWidth: 1200, quality: 85) {
          ...GatsbyImageSharpFluid_withWebp
        }
      }
    }
  }
`
